import cn from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import AppliedOrOfferTag from 'components/common/AppliedOrOfferTag';
import BodyModal from 'components/common/modals/BodyModal';
import { COLOR_SCHEME_CREAM, COLOR_SCHEME_TEMPO_GRAY } from 'components/common/modals/Modal';
import CaretDown from 'components/icons/navigation/CaretDown';
import CaretRight from 'components/icons/navigation/CaretRight';
import { TEMPO } from 'context/BrandContext';
import { basketDeliveryDate } from 'helpers/timeFormat';
import useBrand from 'hooks/useBrand';
import { logEvent } from 'services/analytics';
const DISCLAIMER_LINE_HEIGHT = 18;
const DISCLAIMER_MAX_LINES = 3;
const DISCLAIMER_MAX_HEIGHT = DISCLAIMER_LINE_HEIGHT * DISCLAIMER_MAX_LINES;

const DiscountDetailsModal = ({
  appliedOrOfferTagProps = null,
  disclaimer,
  discountsGraphic,
  heading,
  largeSubheading,
  modalButtonsComponent,
  onCloseModal,
  shown,
  subheading,
  hideViewOrderLink = false,
  shouldDisplayModalButtons = false,
  upcomingOrderDeliveryDate,
  upcomingOrderUrlPath,
  viewOrderAnalyticsEvent,
  viewOrderAnalyticsProperties = {},
}) => {
  const isTempo = useBrand(TEMPO);
  const [isMounted, setIsMounted] = useState(false);
  const [disclaimerNeedsTruncation, setDisclaimerNeedsTruncation] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const transitionRef = useRef();
  const disclaimerRef = useRef();
  const elementPastDisclaimerRef = useRef();
  const timerRef = useRef();
  const textColor = isTempo ? 'text-tempo-black-100' : 'text-charcoal-100';

  useEffect(() => {
    if (shown && !isMounted) {
      setIsMounted(true);
    } else if (!shown) {
      if (disclaimerNeedsTruncation) {
        setDisclaimerNeedsTruncation(false);
      }
      if (isTruncated) {
        setIsTruncated(false);
      }
      setIsMounted(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }
  }, [shown]);

  useLayoutEffect(() => {
    // After the modal has been initially mounted and opened, determine if truncation is needed
    // for the disclaimer. Update state variables and disclaimer height if applicable.
    const disclaimerContent = disclaimerRef.current;

    if (disclaimerContent) {
      const truncationNeeded = disclaimerContent.scrollHeight > DISCLAIMER_MAX_HEIGHT;
      if (truncationNeeded) {
        setDisclaimerNeedsTruncation(true);
        setIsTruncated(true);
        disclaimerContent.style.height = `${DISCLAIMER_MAX_HEIGHT}px`;
      }
    }
  }, [isMounted]);

  useEffect(() => {
    // After clicking the View More button, if its opening the truncated disclaimer, scroll to the
    // bottom of the modal automatically to view the end of the disclaimer
    if (!isTruncated && elementPastDisclaimerRef.current) {
      elementPastDisclaimerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [isTruncated]);

  const handleResize = () => {
    // On window resize, reset values. Close up truncated disclaimer if open, reevaluate if truncation
    // is still needed, or if it wasn't needed before but now it is.
    const disclaimerContent = disclaimerRef.current;

    if (disclaimerContent) {
      const truncationNeeded = disclaimerContent.scrollHeight > DISCLAIMER_MAX_HEIGHT;
      if (truncationNeeded) {
        setDisclaimerNeedsTruncation(true);
        setIsTruncated(true);
        disclaimerContent.style.height = `${DISCLAIMER_MAX_HEIGHT}px`;
      } else {
        setDisclaimerNeedsTruncation(false);
        setIsTruncated(false);
        disclaimerContent.style.height = `${disclaimerContent.scrollHeight}px`;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onViewMore = () => {
    // If the disclaimer is currently truncated, on click of View More we are expanding the disclaimer.
    // Set the disclaimer to its default height. If the disclaimer is currently expanded, on click of
    // View Less we are minimizing and truncating the disclaimer, so we set it to the truncated max height.
    const disclaimerContent = disclaimerRef.current;

    if (disclaimerContent) {
      if (isTruncated) {
        disclaimerContent.style.height = `${disclaimerContent.scrollHeight}px`;
      } else {
        disclaimerContent.style.height = `${DISCLAIMER_MAX_HEIGHT}px`;
      }

      timerRef.current = setTimeout(() => {
        setIsTruncated(!isTruncated);
      }, 100);
    }
  };

  const logEventOnViewOrder = () => {
    if (viewOrderAnalyticsEvent) {
      logEvent(viewOrderAnalyticsEvent, viewOrderAnalyticsProperties);
    }
  };

  const showViewOrderLink =
    upcomingOrderUrlPath &&
    upcomingOrderDeliveryDate &&
    !shouldDisplayModalButtons &&
    !hideViewOrderLink;

  return (
    <CSSTransition
      in={shown}
      timeout={100}
      classNames="fadeInOut"
      unmountOnExit
      nodeRef={transitionRef}
    >
      <BodyModal
        bodyPadding="pb-6 px-6"
        colorScheme={isTempo ? COLOR_SCHEME_TEMPO_GRAY : COLOR_SCHEME_CREAM}
        containerStyle="!max-w-[624px] !p-6"
        contentClassname={cn('!rounded-lg', isTempo ? '!bg-tempo-gray-40' : '!bg-cream-100')}
        dismissable={true}
        onDismiss={onCloseModal}
        showIcon={false}
        topBarStyle="!px-6 !pt-4 !pb-0"
      >
        <>
          {appliedOrOfferTagProps && (
            <div className="mb-1">
              <AppliedOrOfferTag {...appliedOrOfferTagProps} />
            </div>
          )}

          <div className="flex flex-col gap-3">
            <div>
              <h4 className={cn('h4', textColor)}>{heading}</h4>
              {largeSubheading && (
                <p className={cn('text leading-tight', textColor)}>{largeSubheading}</p>
              )}
              <p className={cn('text-sm', textColor)}>{subheading}</p>
            </div>

            {discountsGraphic && discountsGraphic}

            {disclaimer && (
              <div>
                <p
                  className={cn(
                    'textXs overflow-hidden transition-all ease-in-out [&>a]:no-underline',
                    isTempo
                      ? '[&>a]:text-tempo-blue-90 [&>a]:hover:text-tempo-blue-120'
                      : '[&>a]:text-green-110 [&>a]:hover:text-green-120',
                    textColor,
                    { 'line-clamp-3': isTruncated }
                  )}
                  dangerouslySetInnerHTML={{ __html: disclaimer }}
                  ref={disclaimerRef}
                />

                {disclaimerNeedsTruncation && (
                  <div>
                    <button
                      className={cn(
                        'flex items-center py-1 text-sm font-medium',
                        isTempo
                          ? 'text-tempo-blue-90 hover:text-tempo-blue-120'
                          : 'text-green-110 hover:text-green-120'
                      )}
                      onClick={onViewMore}
                    >
                      View {isTruncated ? 'More' : 'Less'}
                      <div
                        className={cn('transition-all', {
                          'rotate-180 transform': !isTruncated,
                        })}
                      >
                        <CaretDown className="h-5 w-5" />
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}

            {showViewOrderLink && (
              <a
                className={cn(
                  'flex items-center justify-between rounded border border-solid border-charcoal-60 bg-white-100 p-3',
                  isTempo ? 'hover:border-tempo-blue-90' : 'hover:border-green-100'
                )}
                href={upcomingOrderUrlPath}
                onClick={logEventOnViewOrder}
              >
                <div className="flex flex-col">
                  <span className={cn('textMd', textColor)}>View Your Upcoming Order</span>
                  <span className={cn('mt-1 text-sm', textColor)}>
                    {basketDeliveryDate(upcomingOrderDeliveryDate)}
                  </span>
                </div>
                <CaretRight className={cn('h-6 w-6', textColor)} />
              </a>
            )}
            {modalButtonsComponent}
          </div>
          <div ref={elementPastDisclaimerRef} />
        </>
      </BodyModal>
    </CSSTransition>
  );
};

DiscountDetailsModal.propTypes = {
  appliedOrOfferTagProps: PropTypes.object,
  disclaimer: PropTypes.string,
  discountsGraphic: PropTypes.element,
  heading: PropTypes.string.isRequired,
  hideViewOrderLink: PropTypes.bool,
  isClaimable: PropTypes.bool,
  largeSubheading: PropTypes.string,
  ModalButtonsComponent: PropTypes.elementType,
  onCloseModal: PropTypes.func.isRequired,
  shown: PropTypes.bool.isRequired,
  subheading: PropTypes.string.isRequired,
  upcomingOrderDeliveryDate: PropTypes.string,
  upcomingOrderUrlPath: PropTypes.string,
  viewOrderAnalyticsEvent: PropTypes.string,
  viewOrderAnalyticsProperties: PropTypes.object,
};

export default DiscountDetailsModal;
